import React, {type ReactNode, useState} from 'react';

import cs from 'classnames';
import {Link} from 'react-router';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useImportDocument} from 'app/docimport/api';
import {
  convertLocationParamsToFetchParams,
  useDocuments,
} from 'app/documents/api';
import {DocumentUploaderArea} from 'app/general/widgets/DocumentUploaderArea';
import StateButton from 'app/general/widgets/StateButton';
import {HOME_WELCOME_TEAMS} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';

import {ProcedureDefinition} from './Definitions';
import {FullDocItem} from './FullDocItem';
import {SmallTextSpan} from './WelcomeStructure';

import type {ObjectToImport} from 'app/docimport/types';
import type {ContentType} from 'js-libs/sweetcommons/contentType';
import type {DocumentSerializedType, ListResponseType} from 'sweet-types';

interface StatefulObjectToImport extends ObjectToImport {
  content_type: 'procedure' | 'policy';
  promise?: Promise<unknown>;
  response?: string;
  error?: string;
}

type Props = {
  onNext: (location: string) => unknown;
};

export const Imports = ({onNext}: Props) => {
  const [isHinting, setIsHinting] = useState<boolean>(false);
  const [importDocument] = useImportDocument();

  const [docs, setDocs] = useState<Array<StatefulObjectToImport>>([]);
  const {data: importedDocs} = useDocuments(
    convertLocationParamsToFetchParams({folder: '', tag: 'imported'})
  );
  const doImportDocument = (doc, content_type) => {
    return importDocument({
      key: doc.key,
      original_filename: doc.originalFilename,
      target_content_type: content_type,
      teams: [],
      folders: [],
    })
      .unwrap()
      .then(
        ({result}) => {
          setDocs((docs) =>
            docs.map((d) =>
              d.key === doc.key ? {...d, error: undefined, response: result} : d
            )
          );
        },
        (error) => {
          setDocs((docs) =>
            docs.map((d) =>
              d.key === doc.key ? {...d, error: error.message} : d
            )
          );
        }
      );
  };
  return (
    <>
      <div className="row">
        <div className="col">
          <h4 className="mt-4">
            {gt('Or import your existing %(procedures)s', {
              procedures: <ProcedureDefinition />,
            })}
          </h4>
          <p>
            {gt(
              'Upload your Microsoft Word formatted documents and convert them to a %(procedure)s.',
              {
                procedure: (
                  <Link
                    to="https://www.sweetprocess.com/kb/8LBTequD/article/wkf1ocGb5/glossary-of-terms-terminology-used-in-sweetprocess/"
                    target="_blank"
                  >
                    {gt('procedure')}
                  </Link>
                ),
              }
            )}
          </p>
          <p>
            <button
              className={cs('btn btn-link text-muted ml-0 pl-0', {
                'd-none': isHinting,
              })}
              onClick={() => setIsHinting(true)}
            >
              {gt('Show formatting help')}
            </button>
            <span className={cs({'d-none': !isHinting})}>
              {gt(
                'The procedure import system does best when documents are formatted according to step one in our help article here: %s',
                <a href="https://www.sweetprocess.com/kb/8LBTequD/article/JDHAfzDwP/how-do-i-import-a-procedure-into-sweetprocess/">
                  {gt('How to import procedures')}
                </a>
              )}
            </span>
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <DocumentUploaderArea
            onComplete={(doc: ObjectToImport) => {
              setDocs((docs) => [...docs, {...doc, content_type: 'procedure'}]);
              doImportDocument(doc, 'procedure');
            }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <ul className="list-group mt-4">
            {docs.map((doc) => (
              <DocItem key={doc.key} doc={doc} importedDocs={importedDocs} />
            ))}
          </ul>
          <ul className="list-group mt-4">
            {importedDocs != null &&
              importedDocs.results.map((doc) => (
                <FullDocItem key={doc.url} object={doc} />
              ))}
          </ul>
        </div>
      </div>
      <div className="row mt-4 justify-content-end">
        {docs.filter((doc) => doc.error == null).length > 0 ? (
          <SmallTextSpan className="col-sm-auto m-1 mb-3">
            {gt(
              "You don't need to wait for conversion to finish. Start using" +
                ' SweetProcess now and the documents will be imported in the background'
            )}
          </SmallTextSpan>
        ) : null}
        <div className="col-sm-auto">
          <LazyTooltip
            tooltip={gt(
              'Start using SweetProcess without importing any documents.'
            )}
          >
            <button
              className="btn text-secondary btn-link mr-2"
              onClick={() => onNext(HOME_WELCOME_TEAMS)}
            >
              {gt('Skip')}
            </button>
          </LazyTooltip>
          <StateButton
            disabled={importedDocs?.count === 0 && docs.length === 0}
            busyText={gt('Setting up account...')}
            className="btn btn btn-primary"
            onClick={() => {
              onNext(HOME_WELCOME_TEAMS);
            }}
          >
            {ngt('Add Procedure', 'Add Procedures', importedDocs?.count || 0)}
          </StateButton>
        </div>
      </div>
    </>
  );
};

type SelectProps = {
  doc: StatefulObjectToImport;
  importedDocs?: ListResponseType<DocumentSerializedType>;
};

const DocItem = ({doc, importedDocs}: SelectProps) => {
  const isError = doc.error != null;
  const isProcessing = doc.response == null && !isError;
  const ct = getContentType(doc.content_type);

  if (
    importedDocs?.results.filter(
      (imported) => doc.response && imported.html_url.endsWith(doc.response)
    ).length
  ) {
    // don't show this component when the full imported doc is available.
    return null;
  }

  return (
    <DocumentListItem
      name={doc.originalFilename}
      ct={doc.response != null ? ct : undefined}
      isError={isError}
      isLoading={isProcessing}
    >
      <small className="text-muted">
        {doc.response == null
          ? isError
            ? gt('Not yet converted')
            : gt('Converting...')
          : gt('Converted')}
      </small>
    </DocumentListItem>
  );
};

interface DocumentListItemProps {
  name: string;
  ct?: ContentType;
  isError: boolean;
  isLoading: boolean;
  error?: string;
  children?: ReactNode;
}
export function DocumentListItem({
  name,
  ct,
  isError,
  error,
  isLoading,
  children,
}: DocumentListItemProps) {
  return (
    <li
      className={cs(
        'list-group-item list-group-item-action flex-column align-items-start',
        {'border-danger': isError}
      )}
    >
      <div className="d-flex w-100 justify-content-between">
        <h5 className={cs({'text-danger': isError})}>
          <i
            className={cs('mr-1', ct?.icon, {
              'fa fa-circle-notch fa-spin': isLoading,
              'fas fa-exclamation-triangle': isError,
            })}
          />
          {name}
        </h5>
        {children}
      </div>
      {isError ? <p className="text-danger">{error}</p> : null}
    </li>
  );
}
